import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';
import './Calculator.css';

const Calculator = ({ onClose, buttonRef }) => {
  const [expression, setExpression] = useState('');
  const [result, setResult] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        x: rect.left - 290,
        y: rect.top
      });
    }
  }, [buttonRef]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.key >= '0' && e.key <= '9') || e.key === '.') {
        e.preventDefault();
        handleNumber(e.key);
      } else if (['+', '-', '*', '/', 'x', 'X'].includes(e.key)) {
        e.preventDefault();
        let operator = e.key;
        
        switch (e.key) {
          case '*':
          case 'x':
          case 'X':
            operator = '×';
            break;
          case '/':
            operator = '÷';
            break;
          case '-':
            operator = '−';
            break;
          default:
            operator = e.key;
        }
        
        handleOperator(operator);
      } else if (e.key === 'Enter' || e.key === '=') {
        e.preventDefault();
        handleEqual();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleDelete();
      } else if (e.key.toLowerCase() === 'c') {
        e.preventDefault();
        handleClear();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [expression, result]);

  const handleNumber = (num) => {
    setExpression(prev => prev + num);
    setResult('');
  };

  const handleOperator = (op) => {
    if (expression || result) {
      const startValue = result || expression;
      setExpression(startValue + op);
      setResult('');
    }
  };

  const handleEqual = () => {
    try {
      const sanitizedExpression = expression
        .replace(/×/g, '*')
        .replace(/÷/g, '/')
        .replace(/−/g, '-');

      const calculatedResult = eval(sanitizedExpression);
      
      const formattedResult = Number.isInteger(calculatedResult) 
        ? calculatedResult.toString()
        : calculatedResult.toFixed(8).replace(/\.?0+$/, '');

      setResult(formattedResult);
      setExpression(formattedResult);
    } catch (error) {
      setResult('Error');
      console.error('Calculation error:', error);
    }
  };

  const handleClear = () => {
    setExpression('');
    setResult('');
  };

  const handleDelete = () => {
    setExpression(prev => prev.slice(0, -1));
    setResult('');
  };

  return (
    <motion.div
      className={`calculator ${isDragging ? 'dragging' : ''}`}
      drag
      dragMomentum={false}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      style={{ x: position.x, y: position.y }}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ duration: 0.1 }}
    >
      <div className="calculator-header">
        <span>Calculator</span>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <div className="calculator-display">
        <div className="expression">{expression || '0'}</div>
        <div className="result">{result}</div>
      </div>

      <div className="calculator-buttons">
        <button onClick={handleClear} className="button function">C</button>
        <button onClick={handleDelete} className="button function">⌫</button>
        <button onClick={() => handleOperator('÷')} className="button operator">÷</button>
        <button onClick={() => handleOperator('×')} className="button operator">×</button>

        <button onClick={() => handleNumber('7')} className="button">7</button>
        <button onClick={() => handleNumber('8')} className="button">8</button>
        <button onClick={() => handleNumber('9')} className="button">9</button>
        <button onClick={() => handleOperator('−')} className="button operator">−</button>

        <button onClick={() => handleNumber('4')} className="button">4</button>
        <button onClick={() => handleNumber('5')} className="button">5</button>
        <button onClick={() => handleNumber('6')} className="button">6</button>
        <button onClick={() => handleOperator('+')} className="button operator">+</button>

        <button onClick={() => handleNumber('1')} className="button">1</button>
        <button onClick={() => handleNumber('2')} className="button">2</button>
        <button onClick={() => handleNumber('3')} className="button">3</button>
        <button onClick={handleEqual} className="button operator equal">=</button>

        <button onClick={() => handleNumber('0')} className="button span-2">0</button>
        <button onClick={() => handleNumber('.')} className="button">.</button>
      </div>
    </motion.div>
  );
};

export default Calculator; 